import { MGPFallible, MGPOptional, Utils } from '@everyboard/lib';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord } from '../../jscaip/Coord';
import { CoordFailure } from '../../jscaip/Coord';
import { CoordSet } from '../../jscaip/CoordSet';
import { GameStatus } from '../../jscaip/GameStatus';
import { Ordinal } from '../../jscaip/Ordinal';
import { Orthogonal } from '../../jscaip/Orthogonal';
import { Player } from '../../jscaip/Player';
import { PlayerMap } from '../../jscaip/PlayerMap';
import { Rules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { DiaballikFailure } from './DiaballikFailure';
import { DiaballikTranslation } from './DiaballikMove';
import { DiaballikPiece, DiaballikState } from './DiaballikState';
export class VictoryOrDefeatCoords {
    winner;
    constructor(winner) {
        this.winner = winner;
    }
}
export class VictoryCoord extends VictoryOrDefeatCoords {
    coord;
    constructor(winner, coord) {
        super(winner);
        this.coord = coord;
    }
}
export class DefeatCoords extends VictoryOrDefeatCoords {
    allLoserPieces;
    opponentPiecesInContact;
    constructor(loser, allLoserPieces, opponentPiecesInContact) {
        super(loser.getOpponent());
        this.allLoserPieces = allLoserPieces;
        this.opponentPiecesInContact = opponentPiecesInContact;
    }
}
export class DiaballikNode extends GameNode {
}
export class DiaballikRules extends Rules {
    static singleton = MGPOptional.empty();
    static get() {
        if (DiaballikRules.singleton.isAbsent()) {
            DiaballikRules.singleton = MGPOptional.of(new DiaballikRules());
        }
        return DiaballikRules.singleton.get();
    }
    getInitialState() {
        const O = DiaballikPiece.ZERO;
        const Ȯ = DiaballikPiece.ZERO_WITH_BALL;
        const X = DiaballikPiece.ONE;
        const Ẋ = DiaballikPiece.ONE_WITH_BALL;
        const _ = DiaballikPiece.NONE;
        const board = [
            [X, X, X, Ẋ, X, X, X],
            [_, _, _, _, _, _, _],
            [_, _, _, _, _, _, _],
            [_, _, _, _, _, _, _],
            [_, _, _, _, _, _, _],
            [_, _, _, _, _, _, _],
            [O, O, O, Ȯ, O, O, O],
        ];
        return new DiaballikState(board, 0);
    }
    isLegal(move, state) {
        let currentState = state;
        for (const subMove of move.getSubMoves()) {
            const start = subMove.getStart();
            if (state.isNotOnBoard(start)) {
                return MGPFallible.failure(CoordFailure.OUT_OF_RANGE(start));
            }
            const end = subMove.getEnd();
            if (state.isNotOnBoard(end)) {
                return MGPFallible.failure(CoordFailure.OUT_OF_RANGE(end));
            }
            const legality = this.isLegalSubMove(currentState, subMove);
            if (legality.isFailure()) {
                return legality;
            }
            currentState = legality.get();
        }
        return MGPFallible.success(currentState);
    }
    isLegalSubMove(state, subMove) {
        if (subMove instanceof DiaballikTranslation) {
            return this.isLegalTranslation(state, subMove);
        }
        else {
            return this.isLegalPass(state, subMove);
        }
    }
    isLegalTranslation(state, translation) {
        // The origin must be a piece owned by the player
        const start = translation.getStart();
        const startPiece = state.getPieceAt(start);
        if (startPiece.owner.isNone()) {
            return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        if (startPiece.owner === state.getCurrentOpponent()) {
            return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        // The moved piece must not hold the ball
        if (startPiece.holdsBall) {
            return MGPFallible.failure(DiaballikFailure.CANNOT_MOVE_WITH_BALL());
        }
        // The destination must be empty
        const end = translation.getEnd();
        if (state.getPieceAt(end).owner.isPlayer()) {
            return MGPFallible.failure(RulesFailure.MUST_LAND_ON_EMPTY_SPACE());
        }
        const updatedBoard = state.getCopiedBoard();
        updatedBoard[start.y][start.x] = DiaballikPiece.NONE;
        updatedBoard[end.y][end.x] = startPiece;
        const stateAfterTranslation = new DiaballikState(updatedBoard, state.turn);
        return MGPFallible.success(stateAfterTranslation);
    }
    isLegalPass(state, pass) {
        // The origin must be a piece of the player that holds the ball
        const start = pass.getStart();
        const startPiece = state.getPieceAt(start);
        if (startPiece.owner.isNone()) {
            return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        if (startPiece.owner === state.getCurrentOpponent()) {
            return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        Utils.assert(startPiece.holdsBall, 'DiaballikRules: cannot pass without the ball');
        // The destination must be a piece of the player
        const end = pass.getEnd();
        const endPiece = state.getPieceAt(end);
        if (endPiece.owner.isNone()) {
            return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        if (endPiece.owner === state.getCurrentOpponent()) {
            return MGPFallible.failure(DiaballikFailure.CANNOT_PASS_TO_OPPONENT());
        }
        // The straight-line path between origin and destination contains no other piece
        const direction = Ordinal.factory.fromMove(start, end).get();
        const afterStart = start.getNext(direction);
        for (let coord = afterStart; coord.equals(end) === false; coord = coord.getNext(direction)) {
            if (state.getPieceAt(coord) !== DiaballikPiece.NONE) {
                return MGPFallible.failure(DiaballikFailure.PASS_PATH_OBSTRUCTED());
            }
        }
        // We must apply the pass to be able to validate the next submoves
        const updatedBoard = state.getCopiedBoard();
        const withBall = updatedBoard[start.y][start.x];
        const withoutBall = updatedBoard[end.y][end.x];
        updatedBoard[start.y][start.x] = withoutBall;
        updatedBoard[end.y][end.x] = withBall;
        return MGPFallible.success(new DiaballikState(updatedBoard, state.turn));
    }
    applyLegalMove(_move, state, _config, stateAfterSubMoves) {
        // All submoves have already been applied and are stored in stateAfterSubMoves
        // We only have to update the turn
        return new DiaballikState(stateAfterSubMoves.board, state.turn + 1);
    }
    getGameStatus(node) {
        const state = node.gameState;
        const victoryOrDefeat = this.getVictoryOrDefeatCoords(state);
        if (victoryOrDefeat.isPresent()) {
            return GameStatus.getVictory(victoryOrDefeat.get().winner);
        }
        else {
            return GameStatus.ONGOING;
        }
    }
    getVictoryOrDefeatCoords(state) {
        // A player wins when their ball is in the opponent's row
        const ballCoordZero = this.getBallCoordInRow(state, 0, Player.ZERO);
        if (ballCoordZero.isPresent()) {
            return MGPOptional.of(new VictoryCoord(Player.ZERO, ballCoordZero.get()));
        }
        const ballCoordOne = this.getBallCoordInRow(state, state.getHeight() - 1, Player.ONE);
        if (ballCoordOne.isPresent()) {
            return MGPOptional.of(new VictoryCoord(Player.ONE, ballCoordOne.get()));
        }
        // The anti-game rule states that if one player blocks the other and there are three pieces in contact,
        // the blocking player loses
        const defeatCoords = this.getBlockerAndCoords(state);
        return defeatCoords;
    }
    getBallCoordInRow(state, y, player) {
        for (let x = 0; x < state.getHeight(); x++) {
            const piece = state.getPieceAtXY(x, y);
            if (piece.holdsBall && piece.owner === player) {
                return MGPOptional.of(new Coord(x, y));
            }
        }
        return MGPOptional.empty();
    }
    getBlockerAndCoords(state) {
        // The anti-game rule states that if a player forms a line that blocks the opponent,
        // and that if three opponent's pieces are touching the line, then the blocker loses
        // We check this one player at a time.
        // In case both players form a line, the one that just moved wins.
        const blocking = PlayerMap.ofValues(this.getBlockerCoords(state, Player.ZERO), this.getBlockerCoords(state, Player.ONE));
        if (blocking.get(Player.ZERO).isPresent() && blocking.get(Player.ONE).isPresent()) {
            // Both players form a line, so the current player loses
            return blocking.get(state.getCurrentPlayer());
        }
        else if (blocking.get(Player.ZERO).isPresent()) {
            return blocking.get(Player.ZERO);
        }
        else if (blocking.get(Player.ONE).isPresent()) {
            return blocking.get(Player.ONE);
        }
        else {
            return MGPOptional.empty();
        }
    }
    getBlockerCoords(state, player) {
        // We check if:
        //   - there is one player piece in each column
        //   - they are all connected
        //   - at least 3 opponent's pieces are connected
        let opponentsConnected = new CoordSet();
        const blockerCoords = [];
        for (let x = 0; x < state.getWidth(); x++) {
            const connectionInfos = this.getConnectedPieceCoord(x, opponentsConnected, state, player);
            opponentsConnected = connectionInfos.opponentsConnected;
            const coord = connectionInfos.coord;
            if (coord.isPresent()) {
                blockerCoords.push(coord.get());
            }
            else {
                // No piece found in this column
                return MGPOptional.empty();
            }
        }
        if (opponentsConnected.size() >= 3) {
            return MGPOptional.of(new DefeatCoords(player, blockerCoords, opponentsConnected.toList()));
        }
        else {
            return MGPOptional.empty();
        }
    }
    getConnectedPieceCoord(x, opponentsConnected, state, player) {
        for (let y = 0; y < state.getHeight(); y++) {
            const coord = new Coord(x, y);
            if (state.getPieceAt(coord).owner === player) {
                // This is a player piece, it needs to be either in column 0
                // or connected on the left to another player piece for a line to be formed
                if (this.isConnectedOnTheLeft(coord, state, player)) {
                    // The piece is connected, we count the opponents that it touches
                    return {
                        coord: MGPOptional.of(coord),
                        opponentsConnected: this.addConnectedOpponents(coord, opponentsConnected, state, player),
                    };
                }
                else {
                    // This piece is not connected, it is therefore impossible to form a line
                    return { coord: MGPOptional.empty(), opponentsConnected };
                }
            }
        }
        return { coord: MGPOptional.empty(), opponentsConnected }; // No piece found in this column
    }
    isConnectedOnTheLeft(coord, state, player) {
        if (coord.x === 0) {
            // A piece in the first column is considered connected
            return true;
        }
        for (const direction of [Ordinal.LEFT, Ordinal.UP_LEFT, Ordinal.DOWN_LEFT]) {
            const neighbor = coord.getNext(direction);
            if (state.coordIsOwnedBy(neighbor, player)) {
                return true;
            }
        }
        return false;
    }
    addConnectedOpponents(coord, opponentsConnected, state, player) {
        for (const direction of Orthogonal.factory.all) {
            const neighbor = coord.getNext(direction);
            if (state.coordIsOwnedBy(neighbor, player.getOpponent())) {
                opponentsConnected = opponentsConnected.addElement(neighbor);
            }
        }
        return opponentsConnected;
    }
}
